// Monday–Sunday ISO week grouping for daily chart series.
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function isoWeekOf(iso) {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = (dt.getDay() + 6) % 7; // Mon=0
  const monday = new Date(y, m - 1, d - dow);
  const sunday = new Date(y, m - 1, d - dow + 6);
  // UTC maths so BST/GMT changes can't shift the week number by one
  const thursday = Date.UTC(y, m - 1, d - dow + 3);
  const jan1 = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  const week = 1 + Math.floor((thursday - jan1) / 86400000 / 7);
  return { week, monday: toIso(monday), sunday: toIso(sunday) };
}

// Adds week, weekTotal, weekStart/weekEnd (ISO Mon/Sun) and first/last labels of each week in the series.
export function annotateWeeks(series) {
  if (!series.length || !series.every((d) => d.date)) return series;
  const groups = new Map();
  series.forEach((d) => {
    const w = isoWeekOf(d.date);
    const g = groups.get(w.monday) || { ...w, total: 0, first: d.label, last: d.label };
    g.total += Number(d.value) || 0;
    g.last = d.label;
    groups.set(w.monday, g);
  });
  return series.map((d) => {
    const g = groups.get(isoWeekOf(d.date).monday);
    return { ...d, week: g.week, weekTotal: g.total, weekStart: g.monday, weekEnd: g.sunday };
  });
}

export function weekGroups(series) {
  const out = [];
  series.forEach((d) => {
    if (d.week == null) return;
    const last = out[out.length - 1];
    if (last && last.week === d.week) last.x2 = d.label;
    else out.push({ week: d.week, total: d.weekTotal, x1: d.label, x2: d.label, start: d.weekStart, end: d.weekEnd });
  });
  return out;
}

export const fmtCompactGBP = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1000) return `£${(v / 1000).toFixed(a >= 100000 ? 0 : 1)}k`;
  return `£${v.toFixed(0)}`;
};

const ddmm = (iso) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}`;

export function WeekLine({ d, fmt }) {
  if (d?.week == null) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-[11px]" data-testid="tooltip-week-total">
      <span className="text-[#475569]">Week {d.week} <span className="text-[#94A3B8]">(Mon {ddmm(d.weekStart)} – Sun {ddmm(d.weekEnd)})</span></span>
      <span className="font-mono-num font-semibold text-[#0F172A]">{fmt(d.weekTotal)}</span>
    </div>
  );
}

// Week label above a ReferenceArea; shortens to fit narrow weeks (e.g. mobile mini charts).
export function WeekLabel({ viewBox, week, total, fontSize = 9 }) {
  if (!viewBox) return null;
  const { x, y, width } = viewBox;
  const full = `Wk ${week} · ${fmtCompactGBP(total)}`;
  const charW = fontSize * 0.62;
  const pad = 8;
  const room = width - pad;
  const text = full.length * charW <= room ? full
    : `${fmtCompactGBP(total)}`.length * charW <= room ? fmtCompactGBP(total)
    : `W${week}`.length * charW <= room ? `W${week}` : null;
  if (!text) return null;
  return (
    <text x={x + width / 2} y={y - 10} textAnchor="middle" fontSize={fontSize}
      fill="#64748B" fontFamily="JetBrains Mono, monospace" data-testid={`week-label-${week}`}>
      {text}
    </text>
  );
}
